import { Inject, Injectable, Pipe, PipeTransform, SecurityContext, DOCUMENT } from '@angular/core';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CompressedEmojiData, emojis, EmojiService } from '@ctrl/ngx-emoji-mart/ngx-emoji';
import { HelperService } from '../../services/helper.service';

@Injectable({ providedIn: 'root' })
@Pipe({
    name: 'replaceEmojis',
})
export class ReplaceEmojisPipe implements PipeTransform {
    private static cachedEmojiRegex: RegExp;
    private sheet: string;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private sanitizer: DomSanitizer,
        private emojiService: EmojiService,
    ) {}

    public transform(
        html: string,
        isHtml?: boolean,
        escapeTagStyles?: boolean,
        sheet = 'apple',
        isLarge?: boolean,
    ): SafeHtml {
        this.sheet = sheet;
        return this.sanitizer.bypassSecurityTrustHtml(
            escapeTagStyles
                ? this.emojisToImages(HelperService.escapeHtml(html), isHtml, isLarge)
                : this.emojisToImages(html, isHtml, isLarge),
        );
    }

    /**
     * Replaces all unicode emojis available through emoji-mart with a span displaying
     * the image representation of that emoji
     *
     * @param html The original html
     */
    public emojisToImages(html: string, isHtml: boolean, isLarge = false): string {
        // Ensure most html entities are parsed to unicode:
        let div = this.document.createElement('div') as Element;
        div.innerHTML = html;
        html = div.innerHTML;

        if (!isHtml) {
            html = this.sanitizer.sanitize(SecurityContext.HTML, html);
            div = this.document.createElement('div') as Element;
            div.innerHTML = html;
            html = div.innerHTML;
        }

        html = html
            // Replace facebook love emoji
            .replace(/❤/g, '❤️')

            // Replace zero width joins with their unicode representations:
            .replace(/&zwj;/g, '\u200d')

            // Replace &#65279 (Zero Width No-Break Space) which appears around smiles:
            .replace(/\uFEFF/g, '')

            // Replace every emoji with a span with a background image:
            .replace(this.emojiRegex, (unicodeEmoji) => {
                const hexCodeSegments = [];
                let i = 0;
                while (i < unicodeEmoji.length) {
                    const segment = unicodeEmoji.codePointAt(i).toString(16).toUpperCase();
                    hexCodeSegments.push(segment);

                    i += Math.ceil(segment.length / 4);
                }
                const hexCode = hexCodeSegments.join('-');
                const matchingData = this.findEmojiData(hexCode);
                if (matchingData) {
                    const span = document.createElement('span');
                    span.className = 'smile-icon';
                    span.style.width = isLarge ? '32px' : '18px';
                    span.style.height = isLarge ? '32px' : '18px';
                    span.style.display = 'inline-block';
                    span.style.backgroundImage = `url(./sb-tools/theme/images/emojis/${this.sheet}_${
                        isLarge ? 64 : 32
                    }.png?v=2)`;
                    span.style.backgroundSize = `${100 * 61}%`;
                    const multiply = 100 / 60;
                    span.style.backgroundPosition = `${multiply * matchingData.sheet[0]}% ${
                        multiply * matchingData.sheet[1]
                    }%`;
                    span.style.textIndent = '-10000px';
                    span.style.overflow = 'hidden';
                    span.style.verticalAlign = 'top';
                    span.innerText = unicodeEmoji;

                    return span.outerHTML;
                }

                return unicodeEmoji;
            });

        return html;
    }

    /**
     * Regex matching all unicode emojis contained in emoji-mart
     */
    private get emojiRegex(): RegExp {
        if (ReplaceEmojisPipe.cachedEmojiRegex) {
            return ReplaceEmojisPipe.cachedEmojiRegex;
        }

        let characterRegexStrings: string[] = [];
        for (const emoji of emojis) {
            characterRegexStrings.push(
                this.emojiService.unifiedToNative(emoji.unified).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
            );
        }

        characterRegexStrings = characterRegexStrings.sort((a, b) => {
            if (a.length > b.length) {
                return -1;
            }

            if (b.length > a.length) {
                return 1;
            }

            return 0;
        });

        const strings = characterRegexStrings;
        const reString = '(' + strings.join('|') + ')';
        try {
            ReplaceEmojisPipe.cachedEmojiRegex = new RegExp(reString, 'gu');
        } catch {
            ReplaceEmojisPipe.cachedEmojiRegex = new RegExp(reString, 'g');
        }

        return ReplaceEmojisPipe.cachedEmojiRegex;
    }

    /**
     * Find raw emoji-mart data for a specific emoji hex code
     *
     * @param hexCode String representation of the emoji hex code
     */
    private findEmojiData(hexCode: string): CompressedEmojiData {
        for (const emojiData of emojis) {
            if (emojiData.unified === hexCode) {
                return emojiData;
            }

            if (emojiData.skinVariations) {
                for (const skinVariation of emojiData.skinVariations) {
                    if (skinVariation.unified === hexCode) {
                        const skinData = Object.assign({}, emojiData);
                        skinData.sheet = skinVariation.sheet;
                        return skinData;
                    }
                }
            }
        }

        return null;
    }
}
